import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ProductsFilterStateService, ProductFilterSlot } from '../../state/products-filter-state.service';
import { summarizeMulti } from '../../../rates/state/dim-summarizers';
import { StateAvailabilityGridComponent } from '../../../../shared/state-availability/state-availability-grid.component';

type PickerMode = 'multi' | 'single' | 'carrier';

/** Maps this picker's slot `field` to the clearAppliedDimension() key used by
 *  the Active-Filters footprint chips, so the picker can self-collapse when
 *  its own chip is cleared. */
const FIELD_TO_CLEARED_KEY: Partial<Record<keyof ProductFilterSlot, string>> = {
  selectedPremiumTypes: 'prem-type',
  selectedAmBest: 'am-best',
  selectedShareClasses: 'share-class',
  selectedSurrYears: 'surr',
  selectedRiders: 'riders',
  selectedStates: 'state',
};

/**
 * Phase 12 — ProductChecklistPickerComponent.
 *
 * Inline rail picker for /products. Two modes:
 *   · 'multi'  — string[] slot field (amBest, surrYears, shareClasses,
 *                premiumTypes, riders, carriers). Click toggles membership.
 *   · 'single' — string slot field (state). Click sets the value;
 *                clicking the selected one clears it.
 *
 * Reads/writes ProductsFilterStateService directly and bumps
 * requestApplyFilters() so ProductsComponent re-runs its grid filter
 * (the page reads the same slot fields via proxies). Chrome mirrors the
 * rates inline-picker-host "field-row" pattern (rail-forms-polish):
 * a clickable head row (name + right-aligned count pill, or "All" when
 * empty) that expands/collapses a FLUSH body (no border, no box, no
 * footer, no duplicate title). Auto-expands when a selection exists.
 * No pin (Phase 12 decision A — pinning deferred for /products) — so
 * the head button is full-width with no ★ star.
 */
@Component({
  selector: 'app-product-checklist-picker',
  standalone: true,
  imports: [CommonModule, StateAvailabilityGridComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="rail-field" [class.is-open]="open() || headless()">
      <!-- The head row is suppressed where this picker IS its group's body —
           a group holding one dimension otherwise nests a second accordion
           whose only job is to open the thing the group already promised, and
           repeats the group's own summary a line below it. See the headless
           input below. -->
      @if (!headless()) {
        <button type="button" class="rail-field-head" [attr.aria-expanded]="open()" (click)="toggleOpen()">
          <span class="rf-name" [innerHTML]="highlightedLabel()"></span>
          <!-- 2026-06-04 icing #4: data-aware summary on products rail too.
               Empty → muted+italic "All"; active → blue value string
               (e.g. "Allianz +2", "7, 10"). Mirrors the rates rail. -->
          @if (selectedCount() === 0) {
            <span class="rf-allnone">All</span>
          } @else {
            <span class="rf-summary">{{ summaryText() }}</span>
          }
        </button>
      }
      @if (headless() || open()) {
        <div class="rail-field-body" [class.is-state-grid]="bodyVariant() === 'stateGrid'">
        @if (bodyVariant() === 'stateGrid') {
          <!-- State Availability renders the shared 50-state grid instead of a
               checklist, so /products, /rates and /changes offer one control.
               Only the BODY changes — the head row, summary, auto-expand and
               chip-clear collapse above are the same as every other field on
               this rail, and toggle() below is the same multi-select write. -->
          <app-state-availability-grid
            [selected]="selectedCodes()"
            (stateToggle)="toggle($event)" />
        } @else {
          @if (showSearch()) {
            <div class="search-wrap">
              <svg class="search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <circle cx="11" cy="11" r="7"/><path d="M21 21l-4.35-4.35"/>
              </svg>
              <input class="search-input" type="text" placeholder="Search…"
                     [value]="searchText()" (input)="onSearchInput($any($event.target).value)"
                     aria-label="Search options" />
            </div>
          }
          <ul class="checklist" role="listbox" [attr.aria-multiselectable]="mode === 'multi'">
            @for (opt of filtered(); track opt) {
              <li class="option" role="option"
                  [class.on]="isSelected(opt)" [attr.aria-selected]="isSelected(opt)"
                  (click)="toggle(opt)">
                <span class="cb" [class.radio]="mode === 'single'" aria-hidden="true">{{ isSelected(opt) ? '✓' : '' }}</span>
                <span class="label">{{ labelFor(opt) }}</span>
              </li>
            } @empty {
              <li class="empty">No options.</li>
            }
          </ul>
        }
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }
    ::ng-deep mark.ff-hit {
      background: #FEF6C8; color: inherit; padding: 0 1px;
      border-radius: 2px; font-weight: 700;
    }
    .rail-field-head {
      width: 100%;
      display: flex; align-items: center; gap: 8px;
      padding: 7px 6px;
      background: transparent; border: none; cursor: pointer;
      font: inherit; text-align: left; border-radius: 6px;
      &:hover { background: var(--bg); }
    }
    .rf-name { font-size: 12.5px; font-weight: 600; color: var(--navy); }
    .rail-field:not(.is-open) .rf-name { font-weight: 500; color: var(--text2); }
    .rf-count {
      margin-left: auto;
      font-family: var(--mono);
      font-size: 10px; font-weight: 600;
      color: var(--blue); background: var(--blue-soft);
      border: 1px solid var(--blue-border);
      border-radius: 9px; padding: 0 6px;
    }
    /* 2026-06-04 icing #4: data-aware summary (replaces count chip).
       Matches the rates rail .rf-summary. */
    .rf-summary {
      margin-left: auto;
      font-size: 11.5px;
      font-weight: 600;
      color: var(--blue);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 150px;
      letter-spacing: 0;
    }
    .rf-allnone { margin-left: auto; font-size: 11px; color: var(--muted); font-style: italic; }
    .rail-field-body {
      padding: 2px 2px 8px 8px;
      max-height: 50vh; overflow-y: auto;
    }
    /* FULL-BLEED FOR THE STATE GRID, so it is the same width here as on
       /rates rather than 41px narrower.

       Both rails hang off the same 280px .rail-col, and both wrap the picker
       body in an identical .rail-field-body (padding 2px 2px 8px 8px).
       Everything above that differs:

         /rates    .filters-body    0 / 0   +  .tp-group-body      0 / 0
         /products .section-body    5 / 8   +  .rail-group-body   14 / 14

       so /rates reaches the picker at 0 a side and /products at 19 left,
       22 right. Cancelling exactly those two paddings puts the grid's content
       box at 20 left / 14 right on both pages — .rail-field-body's 8/2 plus
       the control's own 12/12 — for an identical 246px of grid.

       ONLY THE BODY. Bleeding the head row too would drag the "State" label
       19px left of Riders, Surrenders and every other field on this rail, so
       the head keeps the group's padding and stays aligned with its siblings.

       Same device as .carrier-picker-wrap in the rail's own stylesheet, and
       the same fragility: these two numbers are the sum of the ancestor
       paddings named above. If either moves, recompute them. */
    .rail-field-body.is-state-grid {
      margin-left: -19px;
      margin-right: -22px;
    }
    .search-wrap { position: relative; display: flex; align-items: center; margin: 4px 4px 4px 0; }
    .search-icon { position: absolute; left: 10px; color: var(--muted); pointer-events: none; }
    .search-input {
      width: 100%; height: 28px; border: 1px solid var(--border);
      border-radius: 14px; padding: 0 10px 0 28px; background: var(--panel);
      font-family: inherit; font-size: 12px; color: var(--text2); outline: none;
      &::placeholder { color: var(--muted); }
      &:focus { border-color: var(--blue); box-shadow: 0 0 0 3px rgba(42, 106, 168, 0.18); }
    }
    .checklist { list-style: none; margin: 0; padding: 4px 0; overflow-y: auto; max-height: 320px; }
    .option {
      display: flex; align-items: center; gap: 8px; padding: 6px 6px;
      font-size: 12px; color: var(--text2); cursor: pointer; user-select: none;
      border-radius: 4px;
      &:hover { background: var(--bg); color: var(--navy); }
      &.on { color: var(--navy); font-weight: 600; }
      .cb {
        width: 14px; height: 14px; border-radius: 2px; border: 1px solid var(--border);
        display: grid; place-items: center; font-size: 9px; color: #fff;
        flex-shrink: 0; background: var(--panel);
        &.radio { border-radius: 50%; }
      }
      &.on .cb { background: var(--navy); border-color: var(--navy); }
      .label { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    }
    .empty { padding: 12px 6px; font-size: 11.5px; color: var(--muted); font-style: italic; }
  `],
})
export class ProductChecklistPickerComponent {
  @Input({ required: true }) field!: keyof ProductFilterSlot;
  @Input({ required: true }) label!: string;
  /** Signal input: option lists arrive late (dynamic, after data load),
   *  so this MUST be a signal — a plain @Input would leave the filtered/
   *  showSearch computeds caching the initial empty value. */
  readonly options = input<string[]>([]);
  @Input() mode: PickerMode = 'multi';
  /**
   * Which control fills the body. 'checklist' is the default vertical list;
   * 'stateGrid' swaps in the shared 50-state grid for State Availability.
   *
   * A variant rather than a second component: the head row, the auto-expand
   * rule, the chip-clear collapse and the multi-select write are all identical,
   * and only the body differs. Duplicating this component to change one block
   * is how the three pages' state filters drifted apart in the first place.
   */
  readonly bodyVariant = input<'checklist' | 'stateGrid'>('checklist');

  /**
   * Render the body only — no head row, always expanded.
   *
   * For a group that holds exactly ONE dimension, the head row is a second
   * accordion inside the first: the reader clicks "Riders & Features", then
   * clicks "Riders" to reach the options the group already named, under a label
   * that repeats the group's. The group header carries the same summary
   * (featuresBadge), so nothing is lost by dropping it.
   *
   * Leaves open/collapse state untouched rather than forcing it — there is no
   * control to collapse with, so `open()` simply stops being consulted.
   */
  readonly headless = input(false);
  /** find-a-filter query — highlights the matched substring in the
   *  header label (Phase 12.2). Signal input so the computed reacts. */
  readonly highlightQuery = input<string>('');
  /**
   * Display overrides keyed by raw option value, for values that shouldn't be
   * shown verbatim (Surr Years' -3 sentinel → "Unlimited"). The stored filter
   * value stays the raw string, so the predicate and counts are untouched.
   * Signal input so late-arriving maps re-render.
   */
  readonly labelMap = input<Record<string, string>>({});
  @Output() closeRequest = new EventEmitter<string>();

  private readonly filterState = inject(ProductsFilterStateService);
  protected readonly searchText = signal('');

  /** Collapse state — mirrors the rates inline-picker-host. Auto-expand
   *  on selection unless the user has explicitly toggled. */
  private readonly _userToggled = signal(false);
  private readonly _expanded = signal(false);

  constructor() {
    // Collapse this field when its Active-Filters chip is cleared
    // (clearAppliedDimension → dimClearedRequest). Forces collapse regardless
    // of prior expand state, so a section the user left open also closes on
    // chip-clear. In-picker unchecking does NOT bump the signal (and toggle()
    // pins the picker open on interaction), so interacting with the picker
    // leaves it open.
    effect(() => {
      const req = this.filterState.dimClearedRequest();
      untracked(() => {
        if (req.tick === 0) return;
        const clearedKey = this.mode === 'carrier'
          ? 'company'
          : FIELD_TO_CLEARED_KEY[this.field];
        if (clearedKey && clearedKey === req.key) {
          this._userToggled.set(true);
          this._expanded.set(false);
        }
      });
    });
  }

  protected open(): boolean {
    if (this._userToggled()) return this._expanded();
    return this.selectedCount() > 0;
  }

  toggleOpen(): void {
    // Capture the currently-visible state BEFORE marking user-toggled, so
    // the first click on an auto-expanded field collapses it (not a no-op).
    const wasOpen = this.open();
    this._userToggled.set(true);
    this._expanded.set(!wasOpen);
  }

  /** Header label with the find-a-filter match wrapped in <mark>. */
  protected readonly highlightedLabel = computed<string>(() => {
    const text = this.label;
    const q = this.highlightQuery().trim();
    const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    if (!q) return esc(text);
    const lc = text.toLowerCase();
    const lcq = q.toLowerCase();
    let out = '';
    let i = 0;
    while (i < text.length) {
      const hit = lc.indexOf(lcq, i);
      if (hit === -1) { out += esc(text.slice(i)); break; }
      out += esc(text.slice(i, hit));
      out += '<mark class="ff-hit">' + esc(text.slice(hit, hit + q.length)) + '</mark>';
      i = hit + q.length;
    }
    return out;
  });

  /** Display text for an option — raw value unless overridden by labelMap. */
  labelFor(opt: string): string {
    return this.labelMap()[opt] ?? opt;
  }

  protected readonly filtered = computed<string[]>(() => {
    const q = this.searchText().toLowerCase().trim();
    const all = this.options();
    // Match on the DISPLAYED text, so searching "unlimited" finds the option
    // whose raw value is "-3".
    return q ? all.filter((o) => this.labelFor(o).toLowerCase().includes(q)) : all;
  });

  protected readonly showSearch = computed<boolean>(() => this.options().length > 8);

  /** Current selection for this field. carrier → companyFilter.companies
   *  (string[]); multi → the string[] field; single → the string field. */
  protected readonly selectedValue = computed<string[] | string>(() => {
    this.filterState.version();
    if (this.mode === 'carrier') {
      return this.filterState.slot.companyFilter?.companies ?? [];
    }
    return this.filterState.slot[this.field] as string[] | string;
  });

  /** Selection as a code array, for the stateGrid body. */
  protected readonly selectedCodes = computed<string[]>(() => {
    const v = this.selectedValue();
    return Array.isArray(v) ? v : (v ? [v] : []);
  });

  protected selectedCount(): number {
    const v = this.selectedValue();
    return Array.isArray(v) ? v.length : (v ? 1 : 0);
  }

  /** 2026-06-04 icing #4 — data-aware summary string for the header right
   *  side. Multi-select arrays follow "first +N" convention; single-select
   *  shows the value verbatim. Empty values render via .rf-allnone, so this
   *  computed never returns 'All'. */
  protected readonly summaryText = computed<string>(() => {
    const v = this.selectedValue();
    // Map through labelFor so the header reads "Unlimited", not "-3".
    if (Array.isArray(v)) return summarizeMulti(v.map((o) => this.labelFor(o)));
    return v ? this.labelFor(v) : '';
  });

  isSelected(opt: string): boolean {
    const v = this.selectedValue();
    return Array.isArray(v) ? v.includes(opt) : v === opt;
  }

  onSearchInput(v: string): void {
    this.searchText.set(v);
  }

  toggle(opt: string): void {
    // Pin the picker open on first item interaction so that clearing this filter
    // externally (via chip ×) keeps the body visible with items shown as unchecked,
    // rather than auto-collapsing the picker body.
    if (!this._userToggled()) {
      this._userToggled.set(true);
      this._expanded.set(true);
    }
    if (this.mode === 'single') {
      const cur = this.selectedValue() as string;
      this.writeField(cur === opt ? '' : opt);
    } else if (this.mode === 'carrier') {
      const cur = this.selectedValue() as string[];
      const next = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
      this.writeCarrier(next);
    } else {
      const cur = this.selectedValue() as string[];
      this.writeField(cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
    }
  }

  clearAll(): void {
    if (this.mode === 'carrier') { this.writeCarrier([]); return; }
    this.writeField(this.mode === 'single' ? '' : []);
  }

  private writeField(value: string[] | string): void {
    this.filterState.setSlotField(this.field, value as ProductFilterSlot[keyof ProductFilterSlot]);
    this.filterState.requestApplyFilters();
  }

  /** Carrier writes the companyFilter object. Empty products[] means
   *  "match all products of the selected companies" (page predicate
   *  skips the product check when products is empty). */
  private writeCarrier(companies: string[]): void {
    this.filterState.companyFilter = companies.length ? { companies, products: [] } : null;
    this.filterState.requestApplyFilters();
  }
}
